// src/domains/core/context-intelligence/services/insightGenerationService.ts

import type { Insight, InsightSource } from "../types/insights.types";

import { PriorityEngine } from "../engine/priorityEngine";
import { RuleEngine } from "../engine/ruleEngine";
import { ruleRegistry } from "../registry/ruleRegistry";
import { insightService } from "./insightService";

/**
 * Rule operand interface for extracting indicator IDs.
 */
interface RuleOperand {
  indicatorId?: string;
  operands?: RuleOperand[];
}

/**
 * Extracts indicator IDs from rule operands.
 *
 * @param operands - Rule operands
 *
 * @param indicatorIds - Array to collect indicator IDs
 *
 * @returns void
 *
 */
function extractIndicatorIds(operands: RuleOperand[], indicatorIds: string[]): void {
  for (const operand of operands) {
    if (operand.indicatorId) {
      indicatorIds.push(operand.indicatorId);
    } else if (operand.operands) {
      extractIndicatorIds(operand.operands, indicatorIds);
    }
  }
}

/**
 * Extracts indicator values from condition results.
 *
 * @param conditionResults - Condition results
 *
 * @returns Record of indicator values
 *
 */
function extractIndicatorValues(
  conditionResults: {
    operand?: unknown;
    value?: null | number;
  }[],
): Record<string, number> {
  const indicatorValues: Record<string, number> = {};
  for (const conditionResult of conditionResults) {
    const operand = conditionResult.operand as
      | null
      | undefined
      | {
          indicatorId?: string;
          value?: number;
        };
    const value = conditionResult.value;
    if (
      operand &&
      typeof operand === "object" &&
      "indicatorId" in operand &&
      operand.indicatorId &&
      typeof value === "number"
    ) {
      indicatorValues[operand.indicatorId] = value;
    }
  }
  return indicatorValues;
}

/**
 * Replaces template variables in text.
 *
 * @param text - Text with template variables
 *
 * @param indicatorValues - Indicator values
 *
 * @param templateMap - Template mapping
 *
 * @returns Text with replaced variables
 *
 */
function replaceTemplateVariables(
  text: string,
  indicatorValues: Record<string, number>,
  templateMap: Record<string, string>,
): string {
  let result = text;
  for (const [indicatorId, value] of Object.entries(indicatorValues)) {
    const templateKey = templateMap[indicatorId] ?? indicatorId;
    const placeholder = `{{${templateKey}}}`;
    const replacement = value.toFixed(0);
    result = result.replaceAll(
      new RegExp(placeholder.replaceAll(/[{}]/g, String.raw`\$&`), "g"),
      replacement,
    );
  }
  return result;
}

/**
 * Insight Generation Service
 * Executes rules and generates insights
 */
export const InsightGenerationService = {
  /**
   * Generate insights from satisfied rules
   * This should be called periodically or on-demand
   *
   * @param getIndicatorValue - Function to get indicator values
   *
   * @returns Array of generated insights
   *
   */
  async generateInsightsFromRules(
    getIndicatorValue: (indicatorId: string) => null | number,
  ): Promise<Insight[]> {
    const generatedInsights: Insight[] = [];

    // Execute all rules
    const executionResults = await RuleEngine.executeSatisfiedRules(getIndicatorValue);

    for (const result of executionResults) {
      if (result.satisfied && result.rule.action.insightTemplate) {
        // Process satisfied rule with insight template
      } else {
        continue;
      }

      // Extract indicator values for template replacement
      const indicatorValues = result.conditionResults
        ? extractIndicatorValues(result.conditionResults)
        : {};

      // Simple template replacement (e.g., {{readiness}} -> actual value)
      // Map indicator IDs to shorter names for templates
      const templateMap: Record<string, string> = {
        oura_activity: "activity",
        oura_readiness: "readiness",
        oura_sleep_score: "sleep_score",
        oura_steps: "steps",
        timetracq_open_tasks: "tasks",
        timetracq_task_completion_rate: "completion",
        timetracq_total_tasks: "total_tasks",
      };

      // Replace template variables
      const title = replaceTemplateVariables(
        result.rule.action.insightTemplate.title,
        indicatorValues,
        templateMap,
      );
      const description = replaceTemplateVariables(
        result.rule.action.insightTemplate.description,
        indicatorValues,
        templateMap,
      );

      // Get indicator IDs from rule conditions
      const indicatorIds: string[] = [];
      extractIndicatorIds(result.rule.condition.operands, indicatorIds);

      // Create insight source
      const source: InsightSource = {
        id: result.rule.id,
        name: result.rule.name,
        type: "rule",
      };

      // Calculate initial relevance and confidence
      const relevance = PriorityEngine.calculateRelevance({
        category: result.rule.action.insightTemplate.category,
        confidence: 0.7,
        description,
        generatedAt: Date.now(),
        id: "",
        indicatorIds,
        priority: result.rule.action.insightTemplate.priority,
        relevance: 0.5,
        sources: [source],
        status: "active",
        title,
        version: 1,
      });

      const confidence = PriorityEngine.calculateConfidence({
        category: result.rule.action.insightTemplate.category,
        confidence: 0.7,
        description,
        generatedAt: Date.now(),
        id: "",
        indicatorIds,
        priority: result.rule.action.insightTemplate.priority,
        relevance,
        sources: [source],
        status: "active",
        title,
        version: 1,
      });

      const priority = PriorityEngine.assignPriority(relevance, confidence);

      // Create insight
      const insight: Omit<Insight, "generatedAt" | "id" | "version"> = {
        category: result.rule.action.insightTemplate.category,
        confidence,
        description,
        indicatorIds,
        metadata: {
          actionable: true,
          ...(result.rule.action.insightTemplate.recommendations !== undefined && {
            recommendations: result.rule.action.insightTemplate.recommendations,
          }),
        },
        priority,
        relevance,
        sources: [source],
        status: "active",
        title,
      };

      try {
        const createdInsight = await insightService.createInsight(insight);

        generatedInsights.push(createdInsight);
      } catch {
        // NOTE: Individual insight creation failures are ignored to allow other rules to continue
      }
    }

    return generatedInsights;
  },

  /**
   * Process rules and generate insights
   * Main entry point for insight generation
   *
   * @param getIndicatorValue - Function to get indicator values
   *
   * @returns Object with execution statistics
   *
   */
  async processRules(getIndicatorValue: (indicatorId: string) => null | number): Promise<{
    executed: number;
    satisfied: number;
    insightsGenerated: number;
  }> {
    const allRules = ruleRegistry.getEnabled();
    const executionResults = await RuleEngine.executeAllRules(getIndicatorValue);
    const satisfiedResults = executionResults.filter((r) => r.satisfied);
    const insights = await this.generateInsightsFromRules(getIndicatorValue);

    return {
      executed: allRules.length,
      insightsGenerated: insights.length,
      satisfied: satisfiedResults.length,
    };
  },
};
